import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import type { AutocompleteProps } from '@mui/material';
import { Field } from 'formik';
import type { FieldProps } from 'formik';
import React, { useState } from 'react';

import InputLabel from '@components/InputLabel/index';

import { DrilldownItem } from '@layouts/DrilldownStyle';

type AutocompleteFieldProps<T> = Pick<
  AutocompleteProps<T, false, true, true>,
  'getOptionLabel'
>;

type AsyncAutocompleteProps<T> = Pick<
  AutocompleteProps<T, true, false, false>,
  'onClose' | 'onOpen' | 'open' | 'loading'
>;
interface FormikAutocompleteFieldProps<T> extends AutocompleteFieldProps<T> {
  /** Is field being edited? */
  isEditing: boolean;
  /** Field label name */
  label: string;
  /** Form name */
  name: string;
  /** Friendly field value when read-only */
  displayValue?: string;
  /** Field value */
  value?: string | null;
  /** List of field options to choose from */
  options: string[];
  /** Original list of options */
  optionMap?: Record<string, string>;
  /** Is this field required */
  required?: boolean;
  /** Callback to change field value */
  onChange: (value: string) => void;
  //   readOnlyComponent?: React.ReactNode;
  async?: AsyncAutocompleteProps<T>;
}
export default function FormikAutocompleteField<T>({
  async,
  displayValue,
  getOptionLabel,
  isEditing,
  label,
  name,
  onChange: handleChange,
  options,
  //   readOnlyComponent,
  required,
  value,
}: FormikAutocompleteFieldProps<T>) {
  const [searchText, setSearchText] = useState<string>('');

  return (
    <>
      {isEditing ? (
        <>
          <InputLabel
            label={label}
            required={required}
            style={{ color: 'primary.main' }}
          />
          <Autocomplete
            {...async}
            options={options}
            getOptionLabel={getOptionLabel}
            onChange={(_event, value) => {
              if (value) {
                handleChange(value);
              }
            }}
            renderInput={(params) => (
              <Field name={name}>
                {({ field, meta }: FieldProps<string>) => (
                  <TextField
                    {...params}
                    {...field}
                    error={Boolean(meta.error)}
                    helperText={meta.touched ? meta.error : ''}
                    margin="dense"
                    sx={({ palette: { error } }) => ({
                      ...(Boolean(meta.error) && {
                        '& .MuiInputBase-root': {
                          border: `1px solid ${error.main}`,
                          borderRadius: '4px',
                        },
                      }),
                    })}
                    InputProps={{
                      ...params.InputProps,
                      className:
                        meta.error && meta.touched ? 'error' : undefined,
                      endAdornment: (
                        <React.Fragment>
                          {async?.loading ? (
                            <CircularProgress
                              color="inherit"
                              size={20}
                              sx={{ mr: 3 }}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    onChange={(event) => {
                      const { value } = event.target;
                      setSearchText(value);
                    }}
                    value={searchText}
                  />
                )}
              </Field>
            )}
            size="small"
            sx={{ width: '100%' }}
            value={value}
          />
        </>
      ) : (
        <DrilldownItem
          label={label}
          name={name}
          readOnly
          value={displayValue}
        />
      )}
    </>
  );
}
